import { HttpErrorResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Action } from '@ngrx/store'
import { from, Observable, of, throwError } from 'rxjs'
import { catchError, concatMap, map, mergeMap, retryWhen } from 'rxjs/operators'
import {
  ACCOUNT_LINK_TYPE,
  BulkCountsResponseDto,
  CountsResponseDto,
  commentaryCountKey,
  HttpCommentaryCountsService,
} from '@helios/common-api'
import {
  commentaryAccountCountsLoaded,
  commentaryAccountCountsSettled,
  commentaryCountsLoadFailed,
  commentaryCountsLoaded,
  commentaryCountsSettled,
  loadCommentaryAccountCounts,
  loadCommentaryCounts,
} from './commentary-counts.reducer'

// Must match commentary-ws's CommentsResource.BULK_COUNTS_MAX_ACCOUNTS — the
// server hard-rejects a bulk request over this size with a 400.
export const BULK_COUNTS_MAX_ACCOUNTS = 200

export const BULK_COUNTS_RETRY_COUNT = 2

function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size))
  }
  return chunks
}

@Injectable()
export class CommentaryCountsEffects {
  // DLITE-5094: result precedence. Loads of one account can overlap (a live
  // thread event force-reloads while an earlier load runs, and both effects
  // use mergeMap), and their responses can arrive in either order. Each
  // request takes a sequence number per account and per surface when it is
  // dispatched; only the newest request's outcome is applied. An older
  // response that lands after a newer request was dispatched is replaced by a
  // reducer-less `settled` action, so it cannot restore pre-change counts and
  // observers still see the older request end.
  private requestSeq = 0
  /** accountId → seq of the newest entity request without an outcome yet. */
  private readonly latestEntityRequest = new Map<string, number>()
  /** accountId → seq of the newest totals request without an outcome yet. */
  private readonly latestTotalsRequest = new Map<string, number>()

  loadCounts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCommentaryCounts),
      // mergeMap (not switchMap): counts are now stored per-account, so
      // concurrent loads for different accounts must all complete — switchMap
      // would cancel earlier accounts' fetches.
      mergeMap(({ payload }: { payload: { accountId: string } }) => {
        const seq = this.beginRequest(this.latestEntityRequest, [
          payload.accountId,
        ])
        return this.api.fetchCounts(payload.accountId).pipe(
          map(
            (response: CountsResponseDto): Action =>
              commentaryCountsLoaded({
                accountId: payload.accountId,
                counts: response?.counts ?? {},
              }),
          ),
          catchError((err: HttpErrorResponse) =>
            of(
              commentaryCountsLoadFailed({
                accountId: payload.accountId,
                error: err?.message ?? `HTTP ${err?.status ?? 'unknown'} error`,
                // DLITE-5094: 403 means the user cannot read this account. A
                // 404 is a routing fault (this route has no 404 denial path),
                // not a denial, so it does not mark the account unavailable.
                unavailable: err?.status === 403,
                source: 'single',
              }),
            ),
          ),
          map((outcome: Action) =>
            this.takeOutcome(this.latestEntityRequest, payload.accountId, seq)
              ? outcome
              : commentaryCountsSettled({ accountId: payload.accountId }),
          ),
        )
      }),
    ),
  )

  // One bulk request per batch rather than one HTTP call per account; commentary-ws fans
  // the DynamoDB reads out server-side.
  //
  // Only an id in the response's `failed` list sinks that account, emitting
  // commentaryCountsLoadFailed so the service drops its dedup marker and the next
  // modelUpdated retries. An id absent from BOTH `counts` and `failed` is a real
  // zero-comment account, not a failure: bulkCounts writes a `counts[id]` entry for every
  // member id it resolves, so absence from both cannot mean "we didn't get to it", and
  // treating it as failure would re-request forever.
  loadAccountCounts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCommentaryAccountCounts),
      mergeMap(({ payload }: { payload: { accountIds: string[] } }) => {
        const seq = this.beginRequest(
          this.latestTotalsRequest,
          payload.accountIds,
        )
        // Split, because commentary-ws 400s a bulk request over the cap.
        return from(chunk(payload.accountIds, BULK_COUNTS_MAX_ACCOUNTS)).pipe(
          mergeMap(accountIds =>
            this.api.fetchBulkCounts(accountIds).pipe(
              // Retried per chunk because a bulk failure blanks every badge in it at
              // once, not one. 404 must fall through: it means the route isn't deployed
              // yet, and retrying would triple the POSTs the branch below exists to stop.
              retryWhen((errors: Observable<HttpErrorResponse>) =>
                errors.pipe(
                  concatMap((err: HttpErrorResponse, attempt: number) =>
                    attempt < BULK_COUNTS_RETRY_COUNT && err?.status !== 404
                      ? of(err)
                      : throwError(err),
                  ),
                ),
              ),
              mergeMap((response: BulkCountsResponseDto) => {
                const counts = response?.counts ?? {}
                const failed = new Set(response?.failed ?? [])
                const loaded: Record<string, number> = {}
                const actions: Action[] = []
                for (const accountId of accountIds) {
                  if (failed.has(accountId)) {
                    actions.push(
                      commentaryCountsLoadFailed({
                        accountId,
                        error: 'account denied (present in bulk failed list)',
                        unavailable: true,
                        // A bulk `failed` entry may be transient (a helios
                        // membership hiccup), so the live service retries it.
                        retryable: true,
                        source: 'bulk',
                      }),
                    )
                    continue
                  }
                  loaded[accountId] =
                    counts[accountId]?.[
                      commentaryCountKey(ACCOUNT_LINK_TYPE, accountId)
                    ] ?? 0
                }
                if (Object.keys(loaded).length > 0) {
                  actions.unshift(
                    commentaryAccountCountsLoaded({ counts: loaded }),
                  )
                }
                return from(actions)
              }),
              catchError((err: HttpErrorResponse) => {
                // Failing per-account drops the `requestedAccountIds` markers
                // ensureAccountsLoaded dedupes against, so a missing route would
                // re-POST on every grid modelUpdated without bound. So a 404
                // emits no loaded/failed outcome — only the reducer-less
                // `settled` signal, which ends in-flight tracking (DLITE-5094)
                // and leaves every dedup marker in place.
                if (err?.status === 404) {
                  return of(commentaryAccountCountsSettled({ accountIds }))
                }
                return from(
                  accountIds.map(accountId =>
                    commentaryCountsLoadFailed({
                      accountId,
                      error:
                        err?.message ??
                        `HTTP ${err?.status ?? 'unknown'} error`,
                      // DLITE-5094: a whole-request 403 means none of this
                      // chunk is readable by the caller.
                      unavailable: err?.status === 403,
                      source: 'bulk',
                    }),
                  ),
                )
              }),
              mergeMap((outcome: Action) =>
                from(this.keepLatestTotals(outcome, seq)),
              ),
            ),
          ),
        )
      }),
    ),
  )

  constructor(
    private readonly actions$: Actions,
    private readonly api: HttpCommentaryCountsService,
  ) {}

  /** Record a newly dispatched request for `accountIds`; returns its seq. */
  private beginRequest(latest: Map<string, number>, accountIds: string[]) {
    const seq = ++this.requestSeq
    accountIds.forEach(id => latest.set(id, seq))
    return seq
  }

  /** True when request `seq` is still the newest one for `accountId`; its
   *  outcome is then applied and the account has no pending request left.
   *  False when a newer request was dispatched after it. */
  private takeOutcome(
    latest: Map<string, number>,
    accountId: string,
    seq: number,
  ): boolean {
    if (latest.get(accountId) !== seq) return false
    latest.delete(accountId)
    return true
  }

  /** Splits one totals outcome of request `seq` into the part that is still
   *  the newest for its accounts (kept) and the part a newer request has
   *  superseded (reported as `settled`). */
  private keepLatestTotals(outcome: Action, seq: number): Action[] {
    const take = (id: string) =>
      this.takeOutcome(this.latestTotalsRequest, id, seq)
    if (outcome.type === commentaryAccountCountsLoaded.type) {
      const { counts } = (
        outcome as ReturnType<typeof commentaryAccountCountsLoaded>
      ).payload
      const kept: Record<string, number> = {}
      const superseded: string[] = []
      Object.keys(counts).forEach(id => {
        if (take(id)) kept[id] = counts[id]
        else superseded.push(id)
      })
      const actions: Action[] = []
      if (Object.keys(kept).length > 0) {
        actions.push(commentaryAccountCountsLoaded({ counts: kept }))
      }
      if (superseded.length > 0) {
        actions.push(commentaryAccountCountsSettled({ accountIds: superseded }))
      }
      return actions
    }
    if (outcome.type === commentaryCountsLoadFailed.type) {
      const { accountId } = (
        outcome as ReturnType<typeof commentaryCountsLoadFailed>
      ).payload
      return take(accountId)
        ? [outcome]
        : [commentaryAccountCountsSettled({ accountIds: [accountId] })]
    }
    if (outcome.type === commentaryAccountCountsSettled.type) {
      // A settle (404) is no state change, so it is passed on whatever the
      // order; it only ends this request's pending entry where it is newest.
      ;(
        outcome as ReturnType<typeof commentaryAccountCountsSettled>
      ).payload.accountIds.forEach(id => take(id))
    }
    return [outcome]
  }
}
